import {
  STAT_AGGREGATE_LABEL,
  STAT_COLUMN_KIND_LABEL,
  STAT_LEVEL_LABEL,
  statTableOf,
  type StatCard,
  type StatCardList,
  type StatCell,
  type StatColumn,
  type StatDetail,
  type StatFormat,
  type StatTableSpec,
} from '@capsio/contracts';
import { useEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, messageOf } from '../api-client.js';
import { InfoDot } from '../components/InfoDot.js';

/**
 * 「运营统计」页签（C-168 A5、A6）。
 *
 * ## 一 这一页为什么存在（**这一段留在注释里，不上页面**）
 *
 * 统计是企业管理与 AI 运营最重要的手段之一：企业运营中很难每次都去看原始数据，
 * 都是通过统计来看现象。企业级 / 部门级 / 岗位级各有各的统计表，
 * 这部分是管理自动化中要自动完成的内容。
 *
 * **上一版把这一段与下一段印到了页面顶部。那是错的。**
 * 产品页面标识对象，不解释对象——与「判据编号不上产品页面」是同一族。
 * 设计理由属于这里、属于提交说明、属于设计文档，不属于客户看的那一屏。
 *
 * ## 二 校验状态在卡片上，不藏进详情页
 *
 * 统计表不是 SSOT。SSOT 只在过程表这一层成立：一次记录、不可修改（I-5）。
 * 只要去统计就有可能出错，所以统计表的可信度**来自被审过，不来自它存在**。
 *
 * 「审过没有」是这个数可不可信的第一眼判断，与数值同等重要——
 * 藏进详情页等于默认它可信，而看卡片的人不会为了确认这一点点进去。
 *
 * 这层意思在页面上的落点是**徽标右侧的小 i**（`terminology.ts` 的「校验状态」），
 * 不是一段正文。
 *
 * ## 三 这一页只看，不做
 *
 * 没有任何写入控件。统计数据由运营 ATC 每月生成，界面不改数——
 * 一个能在界面上改的统计数就不是统计出来的了。这也是 ADR-0004 的
 * 「看结果、做审批、触发任务」里的第一类，B-1 的边界一字未动。
 *
 * ## 四 `?stat=` 放地址栏
 *
 * 与 `?node=` / `?tab=` 同一条理由：刷新不丢、能收藏、能把「看这个」发给同事。
 * 本组件自己读写这一个参数，不经 `AppShell` 转手——它只属于这一页，
 * 转一手就要在 `AppShell` 上多挂两个 props，而那个文件今天是共享的。
 */

/* ─── 格式化：一处定死，不让各处自己判 ─── */

/** 千分位。**不走 `toLocaleString`**——它的分组随运行环境的区域设置变，截图与测试会飘。 */
function groupDigits(value: number): string {
  const sign = value < 0 ? '-' : '';
  const digits = Math.abs(Math.round(value)).toString();
  return sign + digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * 一格数怎么显示。
 *
 * **`null` 一律「—」，不写 0。** 「这一期没有这个数」与「这个数是 0」在图上
 * 意味着相反的结论（C-099R 三、P-13）——0 是真值，「—」是「不可判定」。
 */
function formatCell(value: StatCell, format: StatFormat): string {
  if (value === null) return '—';
  if (typeof value === 'string') return value === '' ? '—' : value;
  switch (format) {
    case 'percent':
      return `${(value * 100).toFixed(1)}%`;
    case 'decimal1':
      return value.toFixed(1);
    case 'integer':
      return groupDigits(value);
    default:
      return String(value);
  }
}

/**
 * 期次显示成 `YYYY-MM`。
 *
 * **纯字符串截取，不经 `new Date()`**：把 `2026-07-01` 交给 Date 会按运行环境的
 * 时区解释一次，在 UTC-x 的机器上显示成上一个月。业务日已经由服务端按锚点
 * （`Organization.timezone`）算好，前端再解释一次就是第二个归日口径（I-7、ADR-0051）。
 */
function periodLabel(period: string): string {
  return period.slice(0, 7);
}

/** 校验记录的时刻。这一个是**时刻**不是业务日，按本机显示即可。 */
function formatMoment(iso: string): string {
  const at = new Date(iso);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())} ${pad(at.getHours())}:${pad(at.getMinutes())}`;
}

/* ─── 校验状态：卡片与详情页共用同一块 ─── */

/**
 * 三档，**没有第四档**：未核 / 已核一致 / 已核不一致。
 *
 * 「未核」用 ⚠ 而不是灰点：它不是一个中性状态，它是「这个数还没人核过」。
 */
function VerifyBadge({ card }: { card: StatCard }): ReactNode {
  const badge =
    card.verify.lastResult === null ? (
      <span className="badge amb" data-testid={`stat-verify-${card.code}`}>
        ⚠ 未核
      </span>
    ) : card.verify.lastResult === 'consistent' ? (
      <span className="badge ok" data-testid={`stat-verify-${card.code}`}>
        ✓ 已核一致
      </span>
    ) : (
      <span className="badge red" data-testid={`stat-verify-${card.code}`}>
        ✗ 已核不一致
      </span>
    );

  /*
   * 右侧那个小 i 是这一页**唯一解释自己的地方**。
   *
   * 「统计表不是 SSOT、校验状态与数值同等重要」这层意思不写成页面正文——
   * 产品页面标识对象，不解释对象。内容在 `terminology.ts` 的「校验状态」一条，
   * 与全站其他名词同一个来源、同一种形态、同一个尺寸。
   */
  return (
    <>
      {badge}
      <InfoDot term="校验状态" />
    </>
  );
}

/** 来源标注：这十二期是重算出来的还是模拟的。**不显示就等于默认它是重算的。** */
function OriginNote({ card }: { card: StatCard }): ReactNode {
  if (card.dataOrigin === null) return <span className="note">本层级尚无数据</span>;
  if (card.dataOrigin === 'recomputed') return <span className="note">本表各期均由过程表重算</span>;
  if (card.dataOrigin === 'simulated') {
    return <span className="note">本表各期为模拟数据，未从过程表重算</span>;
  }
  return <span className="note">本表各期来源不一致：部分已重算，部分仍为模拟数据</span>;
}

/* ─── 12 期小柱图 ─── */

/**
 * 小柱图。最老一期在左。
 *
 * **`null` 的那一期照样占一格，且画成空格**——空一格与零高一格是两回事：
 * 前者是「这一期没有这个数」，后者是「这一期是 0」。少画一格则整条会悄悄变短，
 * 看的人会以为只有这么多期。
 */
function Spark({
  points,
  label,
}: {
  points: { periodMonth: string; value: number | null }[];
  label: string;
}): ReactNode {
  if (points.length === 0) return <p className="note">尚无期次，画不出趋势</p>;

  const values = points.map((p) => p.value).filter((v): v is number => v !== null);
  const top = values.length === 0 ? 0 : Math.max(...values);
  const latest = points[points.length - 1]?.periodMonth;

  return (
    <>
      <div className="spark" role="img" aria-label={`${label}：${points.length} 期趋势`}>
        {points.map((point) => (
          <i
            key={point.periodMonth}
            className={point.periodMonth === latest ? 'hi' : ''}
            style={{
              height:
                point.value === null || top <= 0
                  ? '1px'
                  : `${Math.max(3, Math.round((point.value / top) * 100))}%`,
              /* 没有值的那一期画成虚线空格，不画成贴底的实心柱。 */
              ...(point.value === null
                ? { background: 'transparent', border: '1px dashed var(--rule2)' }
                : {}),
            }}
            title={`${periodLabel(point.periodMonth)} ${
              point.value === null ? '—' : groupDigits(point.value)
            }`}
          />
        ))}
      </div>
      <div className="note">
        {periodLabel(points[0]!.periodMonth)} → {periodLabel(latest ?? '')} {label}
      </div>
    </>
  );
}

/* ─── 卡片墙 ─── */

function StatCardTile({
  card,
  spec,
  onOpen,
}: {
  card: StatCard;
  spec: StatTableSpec;
  onOpen: () => void;
}): ReactNode {
  return (
    /*
      `data-testid` 是给判定用的锚点。**取「只有这块渲染出来才会出现的标记」**
      （C-164 A4）：编码 `STAT-ENT-001` 这个串会随契约一起打进 bundle，
      特征串分不出「契约进了包」和「界面上真有这块卡」。
    */
    <div className="card" data-testid={`stat-card-${card.code}`}>
      <h3>
        {/* 编号等宽，与 ATC 编码同形态——两者在同一套界面里并排出现。 */}
        <span className="mono">{card.code}</span>
        <span className="sub">{card.name}</span>
        <span style={{ marginLeft: 'auto' }}>
          <VerifyBadge card={card} />
        </span>
      </h3>

      <div className="kv">
        <span className="k">周期</span>
        <span>
          按月 · {card.periodWindow}（库内 {card.periodCount} 期）
        </span>
        <span className="k">最近一期</span>
        <span className="mono">
          {card.latestPeriod === null ? '—' : periodLabel(card.latestPeriod)}
        </span>
      </div>

      <div style={{ marginTop: 8 }}>
        {card.headline.map((item) => (
          <div key={item.key} className="kv" style={{ gridTemplateColumns: '1fr auto' }}>
            <span className="k">
              {item.label}
              {/*
                部门级 / 岗位级一期多行，卡片上这个数是合出来的——**必须写出来是怎么合的**。
                「12 个部门的卡位总数」与「每个部门平均多少」差一个数量级，两个数长得一样。
              */}
              {card.level !== 'ent' && (
                <span className="note">
                  {' '}
                  逐{card.level === 'dep' ? '部门' : '卡'}
                  {STAT_AGGREGATE_LABEL[item.aggregate]}
                </span>
              )}
            </span>
            <b className="mono">{formatCell(item.value, columnFormat(spec, item.key))}</b>
          </div>
        ))}
      </div>

      <Spark points={card.trend} label={card.trendLabel} />

      <div className="kv" style={{ marginTop: 10 }}>
        <span className="k">数据来源</span>
        <span className="note">
          源自 {spec.sources.map((s) => s.table).join('、')}（过程表）· 物理表{' '}
          <span className="mono">{card.physicalTable}</span>
        </span>
      </div>

      <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
        <button type="button" className="btns" onClick={onOpen}>
          看详情
        </button>
        <OriginNote card={card} />
      </div>
    </div>
  );
}

function columnFormat(spec: StatTableSpec, key: string): StatFormat {
  return spec.columns.find((column) => column.key === key)?.format ?? 'integer';
}

/* ─── 详情页七段 ─── */

function Section({
  n,
  title,
  sub,
  children,
}: {
  n: number;
  title: string;
  sub?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <div className="card" data-testid={`stat-section-${n}`}>
      <h3>
        <span className="mono">{n}</span> {title}
        {sub !== undefined && <span className="sub">{sub}</span>}
      </h3>
      {children}
    </div>
  );
}

/** 统计值标识（C-168 A7）。本轮用文字，视觉标识组件由 C-167 出，后续统一挂。 */
function KindTag({ column }: { column: StatColumn }): ReactNode {
  if (column.kind === 'dimension') return null;
  return <span className="note">（{STAT_COLUMN_KIND_LABEL[column.kind]}）</span>;
}

function StatTableDetail({
  detail,
  spec,
  onBack,
}: {
  detail: StatDetail;
  spec: StatTableSpec;
  onBack: () => void;
}): ReactNode {
  const { card, rows, origins } = detail;

  return (
    <>
      <div className="card">
        <h3>
          <button type="button" className="linkbtn" onClick={onBack}>
            ← 运营统计
          </button>
          <span className="mono">{card.code}</span>
          {card.name}
          <span style={{ marginLeft: 'auto' }}>
            <VerifyBadge card={card} />
          </span>
        </h3>
        <div className="kv">
          <span className="k">层级</span>
          <span>{STAT_LEVEL_LABEL[card.level]}</span>
          <span className="k">周期</span>
          <span>按月 · {card.periodWindow}</span>
          <span className="k">物理表</span>
          <span className="mono">{card.physicalTable}</span>
          <span className="k">建设卡</span>
          <span className="mono">{card.constructionAtcCode ?? '—（登记不完整）'}</span>
          <span className="k">运营卡</span>
          <span className="mono">{card.operationalAtcCode ?? '—（登记不完整）'}</span>
        </div>
      </div>

      <Section n={1} title="是什么" sub="这张表回答什么问题">
        <p className="note">{spec.question}</p>
      </Section>

      <Section
        n={2}
        title="数值表"
        sub={`${card.periodCount} 期 × ${spec.columns.length} 列 · 最新一期在最上`}
      >
        {rows.length === 0 ? (
          <div className="val">
            这一层级下还没有任何一期数据。<b>不是 0，是还没有。</b>
          </div>
        ) : (
          <div className="stat-wide">
            <table className="tb">
              <thead>
                <tr>
                  <th>期次</th>
                  {spec.columns.map((column) => (
                    <th key={column.key}>
                      {column.label}
                      <KindTag column={column} />
                    </th>
                  ))}
                  <th>本行来源</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={`${row.periodMonth}-${index}`}>
                    <td className="mono">{periodLabel(row.periodMonth)}</td>
                    {spec.columns.map((column) => (
                      <td
                        key={column.key}
                        className={column.kind === 'dimension' ? undefined : 'mono'}
                      >
                        {formatCell(row.values[column.key] ?? null, column.format)}
                      </td>
                    ))}
                    <td className="note">
                      {origins[index] === 'recomputed' ? '过程表重算' : '模拟数据'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section n={3} title="趋势图" sub={card.trendLabel}>
        <Spark points={card.trend} label={card.trendLabel} />
      </Section>

      <Section n={4} title="计算口径" sub="分子分母各是什么 · 每个数带精确名词">
        <table className="tb">
          <thead>
            <tr>
              <th>列</th>
              <th>名词</th>
              <th>口径</th>
              <th>合并方式</th>
            </tr>
          </thead>
          <tbody>
            {spec.columns.map((column) => (
              <tr key={column.key}>
                <td className="mono">{column.key}</td>
                <td>
                  {column.label}
                  <KindTag column={column} />
                </td>
                <td>{column.definition}</td>
                <td className="note">{STAT_AGGREGATE_LABEL[column.aggregate]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <Section n={5} title="数据来源" sub="源自哪张过程表 · 取数条件 · 物理表名">
        <div className="kv" style={{ marginBottom: 10 }}>
          <span className="k">物理表名</span>
          <span className="mono">{card.physicalTable}</span>
        </div>
        <table className="tb">
          <thead>
            <tr>
              <th>源表（权威）</th>
              <th>取数条件</th>
              <th>库内登记</th>
            </tr>
          </thead>
          <tbody>
            {spec.sources.map((source) => {
              const registered = detail.registeredSources.find((r) => r.table === source.table);
              return (
                <tr key={source.table}>
                  <td className="mono">{source.table}</td>
                  <td>{source.condition}</td>
                  <td>
                    {registered === undefined ? (
                      <span className="badge amb">未登记</span>
                    ) : (
                      <span className="badge ok">已登记</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="note" style={{ marginTop: 8 }}>
          「库内登记」是 <span className="mono">stat_table_source</span> 里有没有这一条。两处
          <b>不合并显示</b>：左边是代码里的口径，右边是库里的事实， 两者不一致本身就是要发现的东西。
        </p>
      </Section>

      <Section n={6} title="重算校验记录" sub="何时校验 · 是否一致 · 不一致的差额">
        {detail.verifications.length === 0 ? (
          <div className="val">
            <b>尚未校验。</b>
            这张表今天才建，第一次重算无历史可比；本轮只保留「未核」状态与这一段的位置。
            校验由运营卡的 TEST 段执行——从过程表重算，与统计表比对，逐次在这里落一行。
          </div>
        ) : (
          <table className="tb">
            <thead>
              <tr>
                <th>期次</th>
                <th>校验时刻</th>
                <th>结果</th>
                <th>不一致的差额</th>
                <th>由谁</th>
              </tr>
            </thead>
            <tbody>
              {detail.verifications.map((row) => (
                <tr key={`${row.periodMonth}-${row.verifiedAt}`}>
                  <td className="mono">{periodLabel(row.periodMonth)}</td>
                  <td className="mono">{formatMoment(row.verifiedAt)}</td>
                  <td>
                    {row.isConsistent ? (
                      <span className="badge ok">一致</span>
                    ) : (
                      <span className="badge red">不一致</span>
                    )}
                  </td>
                  <td>{row.differenceNote ?? '—'}</td>
                  <td className="note">{row.verifiedBy ?? '未记录'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section n={7} title="变更记录" sub="口径改过一次，此前各期的可比性就断了一次">
        {detail.changes.length === 0 ? (
          <div className="val">还没有变更记录。</div>
        ) : (
          <table className="tb">
            <thead>
              <tr>
                <th>序</th>
                <th>时刻</th>
                <th>类别</th>
                <th>说明</th>
                <th>由谁</th>
              </tr>
            </thead>
            <tbody>
              {detail.changes.map((row) => (
                <tr key={row.seq}>
                  <td className="mono">{row.seq}</td>
                  <td className="mono">{formatMoment(row.changedAt)}</td>
                  <td className="mono">{row.changeKind}</td>
                  <td>{row.changeNote}</td>
                  <td className="note">{row.changedBy ?? '未记录'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
    </>
  );
}

/* ─── 页面本体 ─── */

export function StatsPage({
  nodeId,
  rootName,
}: {
  /** 站在树上的哪个节点。企业站为 null——层级由服务端从节点自己是什么推出来。 */
  nodeId: string | null;
  rootName: string;
}): ReactNode {
  const [searchParams, setSearchParams] = useSearchParams();
  const openCode = searchParams.get('stat');

  /*
   * 取回来的东西**连同「它是为哪一次请求取的」一起存**。
   *
   * 不在 effect 里先 `setState(null)` 再发请求：那一下同步 setState 会多引发一轮渲染
   * （`react-hooks/set-state-in-effect`）。改成比对 key——`state.key` 与当前 key 不等
   * 就是「还没到」，渲染读取中。**换节点时旧数据不会闪一下**，这正是那个
   * 先清空的写法本来想解决的问题，只是它用错了手段。
   */
  const listKey = nodeId ?? '';
  const [listState, setListState] = useState<{
    key: string;
    data: StatCardList | null;
    error: string | null;
  }>({ key: '', data: null, error: null });

  useEffect(() => {
    let live = true;
    void api.statTables(nodeId).then(
      (data) => live && setListState({ key: listKey, data, error: null }),
      (cause: unknown) =>
        live && setListState({ key: listKey, data: null, error: messageOf(cause) }),
    );
    return () => {
      live = false;
    };
  }, [nodeId, listKey]);

  const list = listState.key === listKey ? listState.data : null;
  const listError = listState.key === listKey ? listState.error : null;

  const spec = openCode === null ? undefined : statTableOf(openCode);
  /*
   * 换了节点之后，地址栏里那个 `?stat=` 可能指向另一层级的表。
   * **不请求、也不报错**——直接当成没选，回到卡片墙。手敲一个别的编码同理。
   */
  const openable = spec !== undefined && list !== null && spec.level === list.level;
  const detailKey = openable ? `${spec.code}|${listKey}` : '';

  const [detailState, setDetailState] = useState<{
    key: string;
    data: StatDetail | null;
    error: string | null;
  }>({ key: '', data: null, error: null });

  useEffect(() => {
    if (detailKey === '') return;
    let live = true;
    void api.statTable(detailKey.split('|')[0]!, nodeId).then(
      (data) => live && setDetailState({ key: detailKey, data, error: null }),
      (cause: unknown) =>
        live && setDetailState({ key: detailKey, data: null, error: messageOf(cause) }),
    );
    return () => {
      live = false;
    };
  }, [detailKey, nodeId]);

  const detail = detailState.key === detailKey ? detailState.data : null;
  const detailError = detailState.key === detailKey ? detailState.error : null;

  function open(code: string): void {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      next.set('stat', code);
      return next;
    });
  }

  function back(): void {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      next.delete('stat');
      return next;
    });
  }

  if (listError !== null) {
    return (
      <div className="pv22 pv22-stack">
        <div className="card">
          <h3>运营统计</h3>
          <p className="msg">{listError}</p>
        </div>
      </div>
    );
  }

  if (list === null) {
    return (
      <div className="pv22 pv22-stack">
        <div className="card">
          <p className="note">读取中……</p>
        </div>
      </div>
    );
  }

  if (openable && spec !== undefined) {
    return (
      <div className="pv22 pv22-stack">
        {detailError !== null ? (
          <div className="card">
            <h3>
              <button type="button" className="linkbtn" onClick={back}>
                ← 运营统计
              </button>
              <span className="mono">{spec.code}</span>
            </h3>
            <p className="msg">{detailError}</p>
          </div>
        ) : detail === null ? (
          <div className="card">
            <p className="note">读取中……</p>
          </div>
        ) : (
          <StatTableDetail detail={detail} spec={spec} onBack={back} />
        )}
      </div>
    );
  }

  return (
    <div className="pv22 pv22-stack">
      <div className="card">
        <h3>
          运营统计
          <span className="sub">
            {STAT_LEVEL_LABEL[list.level]} · {list.scopeName ?? rootName}
          </span>
        </h3>
      </div>

      {list.rows.length === 0 ? (
        <div className="card">
          <div className="val">这一层级还没有登记任何统计表。</div>
        </div>
      ) : (
        <div className="stat-wall" data-testid="stat-wall">
          {list.rows.map((card) => {
            const cardSpec = statTableOf(card.code);
            if (cardSpec === undefined) return null;
            return (
              <StatCardTile
                key={card.code}
                card={card}
                spec={cardSpec}
                onOpen={() => open(card.code)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
